import { useCallback, useMemo, useState } from 'react'
import { doc } from 'firebase/firestore'
import type { DocumentReference } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { lastVisitAt, selectDigest } from '../lib/whatsNew'
import { useAudience } from './useAudience'
import { useAuth } from './useAuth'
import { useChangelogEntries } from './useChangelogEntries'
import { useFirestoreDoc } from './useFirestore'
import { useSeenWatermark } from './useSeenWatermark'
import type { UserActivity } from '../types'

/**
 * The "What's new since your last visit" digest for the signed-in user. The
 * cutoff is the `whatsNewSeen` watermark — created from the last visit on first
 * sight, then moved only by `markSeen()` — so unread entries don't expire just
 * because daily activity stamps land. `ready` means the header pill may show.
 */

export function useWhatsNew() {
  const { signedInUid } = useAuth()
  const { audience } = useAudience()
  // Internal rollout (#510): the team and the inner circle only, for a couple of
  // weeks. UI-only — entries stay public-read. Drop this to launch for everyone.
  const uid = audience === 'team' || audience === 'inner_circle' ? signedInUid : null
  const [now] = useState(Date.now)
  const activityRef = useMemo(
    () => (uid ? (doc(db, 'users', uid, 'private', 'activity') as DocumentReference<UserActivity>) : null),
    [uid],
  )
  const { data: activity, loading: activityLoading, error: activityError } = useFirestoreDoc<UserActivity>(activityRef)
  const { entries, loading: entriesLoading, error: entriesError } = useChangelogEntries(!!uid)

  const since = lastVisitAt(activity, now)
  const entryTimes = useMemo(() => entries.map(e => e.publishedAt), [entries])
  const newest = entryTimes[0] ?? 0

  const { settled, lastSeen, advanceTo } = useSeenWatermark({
    uid,
    docName: 'whatsNewSeen',
    times: entryTimes,
    active: false,
    itemsLoading: entriesLoading || activityLoading,
    // An errored activity doc would read as "never visited" and baseline at now.
    baseline: activityLoading || activityError || entriesLoading || entriesError ? undefined : (since ?? (newest || now)),
    logTag: 'whatsNew',
  })

  // Applied locally at once, so the banner hides before the write round-trips.
  // Stamps the newest entry's server time, never the device clock, so a fast
  // clock can't hide the next entry; anything published later still counts.
  const [markedUpTo, setMarkedUpTo] = useState(0)
  const markSeen = useCallback(() => {
    if (!newest) return
    setMarkedUpTo(newest)
    advanceTo(newest)
  }, [newest, advanceTo])

  const cutoff = lastSeen === null ? Infinity : Math.max(lastSeen, markedUpTo)
  const awayMs = since === null ? 0 : now - since
  const digest = useMemo(() => selectDigest(entries, { cutoff, awayMs }), [entries, cutoff, awayMs])
  const ready = !!uid && settled && lastSeen !== null

  return {
    digest,
    entries,
    ready,
    unseenCount: ready ? entryTimes.filter(t => t > cutoff).length : 0,
    markSeen,
  }
}
